import { window } from "global";
import { useState, useEffect } from "react";
import { MediaQuery } from "@outplayed/responsive";
import { RampUnit, TagBasedAdUnits } from "@outplayed/ramp";
import SearchBar from "components/SearchBar";
import AppDownloadButton from "components/Buttons/AppDownloadButton";
import { useAdFree } from "lib/hooks/use-ad-free";
import { ReactComponent as RiotLogo } from "svg/riot-logo.svg";
import { GameLibraryContainer } from "@ugitgud/legos/components/home-page/GameLibraryContainer";
import { ExclusiveFeatureCarousel } from "@ugitgud/legos/components/plus/ExclusiveFeatureCarousel";
import { ThetaContainer } from "components/Theta/ThetaContainer";

import wpClient from "lib/wp-client";
import { gql, useQuery } from "@apollo/client";

import { rewriteWPImageUrlToS3 } from "lib/helperFunctions";
import { getArticleUrl } from "lib/helperFunctions";
import { formatDate } from "lib/formatDate";
import { UggLink } from "@ugitgud/legos/ui/nav/Link";

const GET_LATEST_POSTS = gql`
  query GetLatestLolPosts($first: Int!) {
    posts(first: $first, where: { categoryName: "lol" }) {
      nodes {
        id
        slug
        date
        title
        featuredImage {
          node {
            mediaItemUrl
          }
        }
      }
    }
  }
`;

const GET_MAIN_SITE_OPTIONS = gql`
  query getMainSiteOptions {
    mainSiteOptions {
      mainSiteOptions {
        mainSiteColoredLabel
        mainSiteRemixIconClass
        mainSiteStickyExcerpt
        mainSiteStickyLabelIconBorderColor
        mainSiteStickyUrl
      }
    }
  }
`;

const DesktopAppLandingPage = () => {
  const isAdFree = useAdFree();
  const [showAd, setShowAd] = useState(false);

  // fetch 3 posts
  const { data, loading, error } = useQuery(GET_LATEST_POSTS, {
    client: wpClient,
    variables: { first: 3 },
  });

  // Fetch main site options from WP to get necessary data for the above fold news banner
  const { data: optionsData } = useQuery(GET_MAIN_SITE_OPTIONS, {
    client: wpClient,
  });

  const opts = optionsData?.mainSiteOptions?.mainSiteOptions;

  const sticky = {
    label: opts?.mainSiteColoredLabel,
    url: opts?.mainSiteStickyUrl,
    excerpt: opts?.mainSiteStickyExcerpt,
    icon: opts?.mainSiteRemixIconClass,
    color: opts?.mainSiteStickyLabelIconBorderColor,
  };

  // split into first + the other two
  const posts = data?.posts.nodes || [];
  const firstPost = posts[0];
  const otherPosts = posts.slice(1, 3);

  useEffect(() => {
    const resizeHandler = () => setShowAd(window.innerWidth >= 1600);
    window.addEventListener("resize", resizeHandler);
    resizeHandler();
    return () => window.removeEventListener("resize", resizeHandler);
  }, []);

  return (
    <div className="desktop-app-landing-page">
      <div className="landing-background">
        <div className="overlay" />
        <div className="background-image" />
      </div>

      <div className="landing-page-layout-container">
        <div className="desktop-app-landing-page_header">
          <img
            className="ugg-logo"
            src="https://static.bigbrain.gg/assets/lol/performance_analysis/images/UGG_Logo_Blue_3.svg"
            alt="U.GG Logo"
          />
          <div className="header-right-side">
            <p>Auto-import runes & more</p>
            <div className="download-button-container">
              <AppDownloadButton url="/app?utm_source=ugghome&utm_campaign=homelogo" label="Download App" icon="cloud" />
              <RiotLogo className="riot-logo" />
              <p>Riot compliant</p>
            </div>
          </div>
        </div>

        <div className="large-search search-container">
          <SearchBar placeholder="Search Yourself or a Champion" autoFocus searchBarId="landing" />
          <MediaQuery min="THETA_WINDOW" max="DESKTOP_LARGE" renderNullOnFail isClient>
            <ThetaContainer />
          </MediaQuery>
        </div>

        {!isAdFree && showAd && (
          <MediaQuery min="DESKTOP_SMALL" max="DESKTOP_LARGE" renderNullOnFail isClient>
            <RampUnit
              type={TagBasedAdUnits.LeaderboardATF}
              id="home-leaderboard-atf"
              pathname={location.pathname}
              search={location.search}
              styles={{
                marginTop: 40,
                marginBottom: -140,
                width: 728,
                height: 90,
              }}
            />
          </MediaQuery>
        )}

        <div className="[&_section]:!mb-0 mt-16 sm:mt-[180px]">
          <ExclusiveFeatureCarousel showFeaturesLink={false} />
        </div>

        {sticky.label && sticky.url && (
          <div className="mt-[60px] w-full flex justify-center [&_.sticky-section]:!mb-0">
            <a href={sticky.url} className="link-whole">
              <div
                className="sticky-section"
                style={{
                  border: sticky.color ? `1px solid ${sticky.color}` : undefined,
                }}
              >
                <div className="sticky-item-wrapper">
                  <div className="left-side">
                    {sticky.icon && <i className={sticky.icon} style={{ color: sticky.color ?? undefined }} />}
                    <div className="sticky-text-label" style={{ color: sticky.color }}>
                      <p>{sticky.label}</p>
                    </div>
                  </div>
                  <div className="sticky-text">
                    <p>{sticky.excerpt}</p>
                  </div>
                </div>
                <div className="sticky-url">
                  <button>
                    <p>Read More</p>
                    <i class="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              </div>
            </a>
          </div>
        )}

        <div className="news-post-area index-page !mt-[60px]">
          <div className="flex-wrapper items-baseline">
            <h2 className="text-white font-inter text-[24px] font-bold">Latest News</h2>
            <UggLink href={"https://u.gg/news"} className="dark:text-accent-blue-400 font-inter text-[18px] ml-3 hover:underline">
              <p>View All News</p>
            </UggLink>
          </div>
          {!loading && !error && (
            <div className="flex-wrapper">
              {firstPost && (
                <div key={firstPost.slug} className="news-item large">
                  <div
                    className="zoomable-image"
                    style={{
                      backgroundImage: `url(${rewriteWPImageUrlToS3(firstPost.featuredImage?.node?.mediaItemUrl || "")})`,
                    }}
                  />
                  <div className="inner-content">
                    <h3>{firstPost.title.length > 85 ? `${firstPost.title.substring(0, 85)}...` : firstPost.title}</h3>
                    <p className="post-date">{formatDate(firstPost.date)}</p>
                  </div>
                  <a href={getArticleUrl(firstPost.slug)} className="link-whole" />
                </div>
              )}

              <div className="small-content-wrapper">
                {otherPosts.map((post, idx) => (
                  <div key={post.slug} className={`news-item small ${idx === 0 ? "first" : ""}`}>
                    <div
                      className="zoomable-image"
                      style={{
                        backgroundImage: `url(${rewriteWPImageUrlToS3(post.featuredImage?.node?.mediaItemUrl || "")})`,
                      }}
                    />
                    <div className="inner-content">
                      <h3>{post.title.length > 85 ? `${post.title.substring(0, 85)}...` : post.title}</h3>
                      <p className="post-date">{formatDate(post.date)}</p>
                    </div>
                    <a href={getArticleUrl(post.slug)} className="link-whole" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <GameLibraryContainer utmSource="ugghome" utmCampaignGameCode="lol" />
      </div>
    </div>
  );
};

export default DesktopAppLandingPage;
